<template>
  <span
    ref="datePicker"
    class="dateDisplay"
    :class="{
      'hover:outline': isEditable && featureFlags?.powerEditInlineEdit,
      isInvalid,
    }"
  >
    <span @click="showPicker = true" class="no-highlights">{{ formattedDate }}</span>
    <MonthYearPicker
      class="right-0"
      v-if="showPicker && isEditable && featureFlags?.powerEditInlineEdit"
      :hasCurrentOption="hasCurrentOption"
      :year="year"
      :month="month"
      :isCurrent="isCurrent"
      @yearChange="handleYearChange"
      @monthChange="handleMonthChange"
      @currentChange="handleCurrentChange"
      @close="showPicker = false"
    />
  </span>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  computed,
  toRefs,
  ref,
  onMounted,
  onUnmounted,
  watch,
  inject,
} from 'vue'
import { isValid } from 'date-fns'
import { isNull } from 'lodash'
import {
  GlobalEvents,
  useGlobalListeners,
} from '@/composables/useGlobalListeners'
import { FeatureFlags } from '@/types'
import MonthYearPicker from './MonthYearPicker.vue'
import { resumeDateDisplay } from '@/lib/dates/dateHelper'

export default defineComponent({
  name: 'DateDisplay',
  props: {
    date: {
      type: String as PropType<string>,
      required: false,
    },
    dateFormat: {
      type: String as PropType<string>,
      required: false,
      default: 'MMM yyyy',
    },
    updatePath: {
      type: String as PropType<string>,
      required: false,
    },
    hasCurrentOption: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    isEditable: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: true,
    },
    isInvalid: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
  },
  setup(props) {
    const featureFlags = inject<FeatureFlags>('featureFlags')
    const { publishEvent } = useGlobalListeners()
    const { date } = toRefs(props)
    const isCurrent = ref(date.value === null || date.value === 'Present')
    const year = ref<number | null>(null)
    const month = ref<number | null>(null)
    const yearChanged = ref(false)
    const monthChanged = ref(false)
    const datePicker = ref(null)
    const showPicker = ref(false)

    const formattedDate = computed(() => {
      return resumeDateDisplay(date.value, props.hasCurrentOption)
    })

    const clickHandler = (e: MouseEvent) => {
      if (!datePicker.value) return
      if (!datePicker.value.contains(e.target as Node)) {
        showPicker.value = false
      }
    }
    onMounted(() => {
      document.addEventListener('click', clickHandler)
    })
    onUnmounted(() => {
      document.removeEventListener('click', clickHandler)
    })
    const handleMonthChange = (m: number) => {
      isCurrent.value = false
      month.value = m
      updatePath()
      monthChanged.value = true
      if (yearChanged.value) {
        showPicker.value = false
      }
    }
    const handleYearChange = (y: number) => {
      isCurrent.value = false
      year.value = y
      updatePath()
      yearChanged.value = true
      if (monthChanged.value) {
        showPicker.value = false
      }
    }
    const handleCurrentChange = (current: boolean) => {
      isCurrent.value = current
      updatePath()
      if (isCurrent.value) {
        showPicker.value = false
      }
    }
    watch(showPicker, value => {
      if (!value) {
        yearChanged.value = false
        monthChanged.value = false
      }
    })

    watch(
      date,
      date => {
        if (isNull(date) || date === 'Present') {
          isCurrent.value = true
          return
        }
        if (isValid(new Date(date))) {
          isCurrent.value = false
          const [y, m] = date.split('-')
          year.value = Number(y)
          month.value = Number(m) - 1
        }
      },
      { immediate: true }
    )

    const updatePath = () => {
      if (!props.updatePath) return
      let date: null | string = null
      if (!isCurrent.value) {
        if (isNull(year.value) || isNull(month.value)) return
        let jsDate = new Date(year.value, month.value, 15)
        if (isValid(jsDate)) {
          date = jsDate.toISOString()?.split('T')[0] || null
        }
      }
      publishEvent(GlobalEvents.INLINE_EDIT, {
        path: props.updatePath,
        value: date,
      })
    }
    return {
      formattedDate,
      showPicker,
      datePicker,
      handleYearChange,
      handleMonthChange,
      handleCurrentChange,
      isCurrent,
      year,
      month,
      featureFlags,
    }
  },
  components: { MonthYearPicker },
})
</script>

<style lang="postcss" scoped>
.dateDisplay {
  @apply relative outline-primary outline-offset-2 border-b-2 border-transparent;
  &.isInvalid {
    @apply border-jsRed-60 bg-jsRed-20;
  }
}
</style>
